/* ------------------------------------------------------------------
   DryBoss [STUDIOLAB] — custom JS overrides.
   Kept in a separate asset so it survives Concept theme updates.
   NOTE: after a theme update, re-check two things:
     1. the include line in layout/theme.liquid (loads right after theme.js)
     2. the `data-icons-drag` marker on the product-page icon list element
        in sections/main-product.liquid (the <div is="icons-carousel">)
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  // Desktop only. On touch devices the theme already ships a native drag
  // carousel and attaches NO hover listeners — leave it entirely alone.
  var isTouch =
    (window.theme && theme.config && theme.config.isTouch) ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0;
  if (isTouch) return;

  // Scope: ONLY the product-template product-icons block, marked with
  // data-icons-drag in sections/main-product.liquid. Every other carousel
  // (featured product, quick-view modal, collection product cards, media
  // galleries, testimonials, …) is intentionally untouched — they keep the
  // theme's native/hover behavior.
  var SELECTOR = '[is="icons-carousel"][data-icons-drag]';

  function upgrade(el) {
    if (!el || el.dataset.iconsDragReady) return;
    el.dataset.iconsDragReady = '1';

    // 1) Remove the theme's hover auto-scroll: mouseenter jumps the carousel
    //    to the far end, mouseleave snaps it back. The handler refs live on
    //    the instance (set in the IconsCarousel constructor), so we detach
    //    exactly those two and keep the a11y tabindex logic intact.
    if (el.onEnterListener) el.removeEventListener('mouseenter', el.onEnterListener);
    if (el.onLeaveListener) el.removeEventListener('mouseleave', el.onLeaveListener);

    // 2) Click-and-drag scrolling (mobile-like grab-to-pull) over the
    //    container's existing overflow-x. Trackpad swipe / shift-wheel still
    //    work; this just adds mouse grab-drag for everyone on desktop.
    var isDown = false, startX = 0, startScroll = 0, moved = false, pid = null;

    el.addEventListener('pointerdown', function (e) {
      // primary button only; ignore touch (native) and secondary buttons
      if (e.pointerType === 'touch' || (e.button !== undefined && e.button !== 0)) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      pid = e.pointerId;
    });

    el.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 3) {
        moved = true;
        el.classList.add('is-dragging');
        if (el.setPointerCapture) {
          try { el.setPointerCapture(pid); } catch (err) {}
        }
      }
      if (moved) {
        el.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    });

    function end() {
      if (!isDown) return;
      isDown = false;
      if (pid != null && el.releasePointerCapture) {
        try { el.releasePointerCapture(pid); } catch (err) {}
      }
      pid = null;
      el.classList.remove('is-dragging');
    }
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);

    // Swallow the click that ends a drag so a link/label inside an icon does
    // not fire when the user was only dragging. A real click (no movement)
    // passes straight through.
    el.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // No native image ghost-drag while grabbing.
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(upgrade);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Re-apply when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* ------------------------------------------------------------------
   "Tag" pill in block-less sections (Featured Collection, Rich Text, …).
   Concept's stock content sections won't host the Tag theme block, so type
   the section's subheading/text as [[Your Tag Text]] and this turns it into
   the same pill as blocks/tag.liquid. Update-safe: the text is native section
   content; the styling (.tag-pill) lives in custom.css — no stock file edited.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  // Any leaf text element wrapped in [[ ]] becomes a tag pill, so it works in
  // whatever text field a section exposes (subheading, subtitle, heading, …).
  var LEAVES = 'p, span, h1, h2, h3, h4, h5, h6, li, div';
  var MARKER = /^\s*\[\[([\s\S]+?)\]\]\s*$/;

  function convert(el) {
    if (el.childElementCount !== 0 || el.classList.contains('tag-pill')) return;
    var m = el.textContent.match(MARKER);
    if (!m) return;
    el.textContent = m[1].trim();
    el.classList.add('tag-pill');
  }

  function init(root) {
    (root || document).querySelectorAll(LEAVES).forEach(convert);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* ------------------------------------------------------------------
   HOME section 3 — start the collection tab row centred on phones.

   Operator asked for the badge and the tabs centred on mobile. The badge
   is pure CSS, but the tab row cannot be: it measures ~387px against a
   375px screen, so it lives in a horizontal scroll-area and overflows.
   `justify-content: center` does nothing there — the flex container is
   sized to its own content, and the PARENT is the scroller — and CSS has
   no way to set a scroll position. So the row is nudged to its centre
   here instead.

   Deliberately only the STARTING position, per the operator: the row
   still scrolls normally and the tabs keep their size. Once a visitor
   scrolls it themselves we never move it again, so this can't yank the
   row out from under a thumb.
   ------------------------------------------------------------------ */
(function () {
  var SEL = '[id$="__featured_collections_7n4jBK"] .tab-list .scroll-area';
  var PHONE = 767;

  function centre(area) {
    if (area.dataset.slCentred === 'user') return;   // they scrolled it; leave it alone
    if (window.innerWidth > PHONE) return;
    var overflow = area.scrollWidth - area.clientWidth;
    if (overflow <= 0) return;
    area.scrollLeft = overflow / 2;
    area.dataset.slCentred = 'auto';
  }

  function watch(area) {
    if (area.dataset.slWatched) return;
    area.dataset.slWatched = '1';
    area.addEventListener('scroll', function () {
      // ignore the programmatic nudge; anything else is the visitor
      if (area.dataset.slCentred === 'auto') { area.dataset.slCentred = ''; return; }
      area.dataset.slCentred = 'user';
    }, { passive: true });
  }

  function init(root) {
    (root || document).querySelectorAll(SEL).forEach(function (area) {
      watch(area);
      centre(area);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  /* images and webfonts change the row's width after DOMContentLoaded, so
     re-measure once everything has actually landed */
  window.addEventListener('load', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  /* Rotating a phone, or crossing the 767px breakpoint, changes both the
     available width and the overflow — without this the row is left sitting at
     scrollLeft 0. The 'user' guard still applies, so a visitor who has already
     swiped the row keeps their position through a rotation. */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { init(); }, 150);
  }, { passive: true });
})();
